import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Stack,
  Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import VerifiedIcon from '@mui/icons-material/Verified';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import BlockIcon from '@mui/icons-material/Block';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { aiSafety } from '@/api/endpoints';
import type { MedicalJurisdiction } from '@/types/medical';
import { regOps } from '@/api/endpoints';
import { useIsMobile } from '@/hooks/useIsMobile';

type Capability = {
  id: number;
  code: string;
  capability_type: 'transcription' | 'summarization' | 'coding_suggestion' | 'triage' | 'safety_check' | 'translation' | 'other';
  display_name: string;
  model: number;
  jurisdictions: number[];
  requires_human_acceptance: boolean;
  writes_clinical_record: boolean;
  can_affect_prescribing: boolean;
  max_risk_level: 'low' | 'medium' | 'high' | 'critical';
  prompt_template_version: string;
  is_active: boolean;
};

type AIModel = { id: number; name: string; version: string; approved_for_medical_use: boolean };

const CAPABILITY_TYPES = [
  { code: 'transcription',      label: 'Transcription' },
  { code: 'summarization',      label: 'Summarization' },
  { code: 'coding_suggestion',  label: 'Coding suggestion (ICD/CPT)' },
  { code: 'triage',             label: 'Triage' },
  { code: 'safety_check',       label: 'Safety check' },
  { code: 'translation',        label: 'Translation' },
  { code: 'other',              label: 'Other' },
];

const RISK_LEVELS = [
  { code: 'low',      label: 'Low' },
  { code: 'medium',   label: 'Medium' },
  { code: 'high',     label: 'High' },
  { code: 'critical', label: 'Critical' },
];

export function AISafetyCapabilitiesPage() {
  const q = useApiQuery({
    queryKey: ['ai-safety', 'capabilities'],
    queryFn:  aiSafety.capabilities.list as () => Promise<Capability[]>,
  });

  const [editing, setEditing] = useState<Capability | null>(null);
  const [creating, setCreating] = useState(false);
  const [activating, setActivating] = useState<Capability | null>(null);
  const [retiring, setRetiring] = useState<Capability | null>(null);

  const qc = useQueryClient();
  const toast = useToast();

  const activateMut = useApiMutation({
    mutationFn: (id: number) => aiSafety.capabilities.activate(id),
    onSuccess: () => {
      toast.success('Capability activated.');
      qc.invalidateQueries({ queryKey: ['ai-safety', 'capabilities'] });
      setActivating(null);
    },
    onError: (e) => toast.error(e.message),
  });
  const retireMut = useApiMutation({
    mutationFn: (vars: { id: number; reason: string }) =>
      aiSafety.capabilities.retire(vars.id, vars.reason),
    onSuccess: () => {
      toast.success('Capability retired.');
      qc.invalidateQueries({ queryKey: ['ai-safety', 'capabilities'] });
      setRetiring(null);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Clinical AI capabilities"
        subtitle="Each capability is a (model + intended use + jurisdictions) bundle. Activation requires an approved safety case."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New capability
          </Button>
        }
      />

      <DataState query={q} empty={{ title: 'No capabilities defined' }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((c) => (
              <Row key={c.id} capability={c}
                   onEdit={() => setEditing(c)}
                   onActivate={() => setActivating(c)}
                   onRetire={() => setRetiring(c)} />
            ))}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <CapabilityDialog capability={editing}
                          onClose={() => { setEditing(null); setCreating(false); }} />
      )}

      <ConfirmDialog
        open={activating !== null}
        title={`Activate "${activating?.display_name}"?`}
        description="Activation requires an approved safety case for this capability. Once active, clinicians can invoke it."
        confirmLabel="Activate"
        onCancel={() => setActivating(null)}
        onConfirm={() => activating ? activateMut.mutateAsync(activating.id) : Promise.resolve()}
      />
      <ConfirmDialog
        open={retiring !== null}
        title={`Retire "${retiring?.display_name}"?`}
        description="Retirement removes the capability from clinician menus. In-flight events finish; new invocations refuse. Provide a reason for the audit log."
        destructive reasonRequired confirmLabel="Retire"
        onCancel={() => setRetiring(null)}
        onConfirm={(reason) =>
          retiring
            ? retireMut.mutateAsync({ id: retiring.id, reason: reason ?? '' })
            : Promise.resolve()
        }
      />
    </>
  );
}

function Row({
  capability, onEdit, onActivate, onRetire,
}: {
  capability: Capability;
  onEdit: () => void;
  onActivate: () => void;
  onRetire: () => void;
}) {
  const RISK_COLOR: Record<Capability['max_risk_level'], 'default' | 'warning' | 'error'> = {
    low: 'default', medium: 'warning', high: 'error', critical: 'error',
  };

  return (
    <Card sx={{ borderColor: capability.is_active ? 'success.main' : 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle',
            color: capability.is_active ? 'success.main' : 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <VerifiedIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {capability.display_name}
              </Typography>
              <Chip size="small" label={capability.code}
                    variant="outlined" className="tabular" />
              <Chip size="small" label={capability.capability_type.replace(/_/g, ' ')}
                    variant="outlined" sx={{ textTransform: 'capitalize' }} />
              <Chip size="small" label={`risk: ${capability.max_risk_level}`}
                    color={RISK_COLOR[capability.max_risk_level]}
                    variant="outlined" />
              {capability.is_active ? (
                <Chip size="small" color="success" variant="outlined" label="active" />
              ) : (
                <Chip size="small" color="warning" variant="outlined" label="inactive" />
              )}
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {capability.requires_human_acceptance && (
                <Chip size="small" label="human accept required" variant="outlined"
                      sx={{ height: 20 }} />
              )}
              {capability.writes_clinical_record && (
                <Chip size="small" label="writes record" color="warning" variant="outlined"
                      sx={{ height: 20 }} />
              )}
              {capability.can_affect_prescribing && (
                <Chip size="small" label="affects Rx" color="error" variant="outlined"
                      sx={{ height: 20 }} />
              )}
            </Stack>
          </Box>
          {capability.is_active ? (
            <Button size="small" variant="outlined" color="error"
                    startIcon={<BlockIcon />}
                    onClick={onRetire}>
              Retire
            </Button>
          ) : (
            <Button size="small" variant="contained" color="success"
                    startIcon={<PowerSettingsNewIcon />}
                    onClick={onActivate}>
              Activate
            </Button>
          )}
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function CapabilityDialog({
  capability, onClose,
}: { capability: Capability | null; onClose: () => void }) {
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = capability !== null;

  const modelsQ = useApiQuery({
    queryKey: ['ai-safety', 'models'],
    queryFn:  aiSafety.models.list as () => Promise<AIModel[]>,
  });
  const jurisdictionsQ = useApiQuery({
    queryKey: ['regops', 'jurisdictions'],
    queryFn:  regOps.jurisdictions.list as () => Promise<MedicalJurisdiction[]>,
  });

  const [code, setCode] = useState(capability?.code ?? '');
  const [capType, setCapType] = useState(capability?.capability_type ?? 'summarization');
  const [displayName, setDisplayName] = useState(capability?.display_name ?? '');
  const [modelId, setModelId] = useState(capability?.model ? String(capability.model) : '');
  const [jurisdictionIds, setJurisdictionIds] = useState<number[]>(capability?.jurisdictions ?? []);
  const [requiresHuman, setRequiresHuman] = useState(capability?.requires_human_acceptance ?? true);
  const [writesRecord, setWritesRecord] = useState(capability?.writes_clinical_record ?? false);
  const [affectsRx, setAffectsRx] = useState(capability?.can_affect_prescribing ?? false);
  const [maxRisk, setMaxRisk] = useState(capability?.max_risk_level ?? 'low');
  const [promptVersion, setPromptVersion] = useState(capability?.prompt_template_version ?? '');

  useEffect(() => {
    setCode(capability?.code ?? '');
    setCapType(capability?.capability_type ?? 'summarization');
    setDisplayName(capability?.display_name ?? '');
    setModelId(capability?.model ? String(capability.model) : '');
    setJurisdictionIds(capability?.jurisdictions ?? []);
    setRequiresHuman(capability?.requires_human_acceptance ?? true);
    setWritesRecord(capability?.writes_clinical_record ?? false);
    setAffectsRx(capability?.can_affect_prescribing ?? false);
    setMaxRisk(capability?.max_risk_level ?? 'low');
    setPromptVersion(capability?.prompt_template_version ?? '');
  }, [capability?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload = {
        code, capability_type: capType, display_name: displayName,
        model: parseInt(modelId, 10),
        jurisdictions: jurisdictionIds,
        requires_human_acceptance: requiresHuman,
        writes_clinical_record: writesRecord,
        can_affect_prescribing: affectsRx,
        max_risk_level: maxRisk,
        prompt_template_version: promptVersion,
      };
      return isEdit
        ? aiSafety.capabilities.update(capability!.id, payload)
        : aiSafety.capabilities.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Capability saved.' : 'Capability created (inactive — needs safety case).');
      qc.invalidateQueries({ queryKey: ['ai-safety', 'capabilities'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = code.trim() && displayName.trim() && modelId;
  const selectedModel = (modelsQ.data ?? []).find((m) => String(m.id) === modelId);
  const modelNotApproved = selectedModel && !selectedModel.approved_for_medical_use;

  return (
    <Dialog fullScreen={isMobile} open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit capability' : 'New capability'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth required label="Code"
                       value={code}
                       onChange={(e) => setCode(e.target.value.replace(/[^a-z0-9_]/g, '_'))}
                       placeholder="scribe_v1"
                       helperText="Unique stable identifier." />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth select label="Type" value={capType}
                       onChange={(e) => setCapType(e.target.value as Capability['capability_type'])}>
              {CAPABILITY_TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth select label="Max risk level"
                       value={maxRisk}
                       onChange={(e) => setMaxRisk(e.target.value as Capability['max_risk_level'])}>
              {RISK_LEVELS.map((r) => (
                <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required label="Display name"
                       value={displayName}
                       onChange={(e) => setDisplayName(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select required label="Model"
                       value={modelId}
                       onChange={(e) => setModelId(e.target.value)}>
              {(modelsQ.data ?? []).map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  {m.name} v{m.version}
                  {!m.approved_for_medical_use && ' (not approved)'}
                </MenuItem>
              ))}
            </TextField>
            {modelNotApproved && (
              <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                Selected model is not approved for medical use. Activation will be blocked.
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Prompt template version"
                       value={promptVersion}
                       onChange={(e) => setPromptVersion(e.target.value)}
                       placeholder="v1.2.3" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}>
              Available in jurisdictions
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(jurisdictionsQ.data ?? []).map((j) => {
                const selected = jurisdictionIds.includes(j.id);
                return (
                  <Chip
                    key={j.id} label={j.code} className="tabular"
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => setJurisdictionIds(
                      selected
                        ? jurisdictionIds.filter((id) => id !== j.id)
                        : [...jurisdictionIds, j.id]
                    )}
                  />
                );
              })}
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={<Switch checked={requiresHuman}
                               onChange={(_, v) => setRequiresHuman(v)} />}
              label="Requires human acceptance" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={<Switch checked={writesRecord}
                               onChange={(_, v) => setWritesRecord(v)} />}
              label="Writes clinical record" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={<Switch checked={affectsRx}
                               onChange={(_, v) => setAffectsRx(v)} />}
              label="Can affect prescribing" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
